import {
  compareRegimes,
  createMmiAssumptions,
  ENGINE_MMI_2026,
  ENGINE_MMI_2026_CORRECTED,
  ENGINE_VERSION,
  projectMmi,
  socialContributionRateFor,
  type MmiAssumptions,
  type MmiEngine,
  type MmiProjection,
  type RegimeComparison,
} from '@app/finance';
import type { Assumptions, ProjectionInput, RegimeComparisonInput } from '@app/shared';

export { ENGINE_MMI_2026, ENGINE_MMI_2026_CORRECTED, ENGINE_VERSION };
export type { MmiEngine };

/** Une simulation enregistrée porte son moteur ; une valeur inconnue retombe sur la référence. */
export function engineOf(engineVersion: string): MmiEngine {
  return engineVersion === ENGINE_MMI_2026_CORRECTED ? ENGINE_MMI_2026_CORRECTED : ENGINE_MMI_2026;
}

/** `taxResidency` pilote `socialContributionRate` ; le moteur ne connaît que le taux. */
export function computeRegimeComparison(
  input: RegimeComparisonInput,
  engine: MmiEngine = ENGINE_MMI_2026,
): RegimeComparison {
  const { taxResidency, ...rest } = input;
  return compareRegimes(
    { ...rest, socialContributionRate: socialContributionRateFor(taxResidency) },
    { engine },
  );
}

/**
 * Les hypothèses globales (inflation, amortissement du bâti…) viennent de `GET /assumptions` :
 * une simulation ne les ressaisit pas, elle ne saisit que les données propres au bien.
 */
export function computeProjection(
  input: ProjectionInput,
  globalAssumptions: Assumptions,
  engine: MmiEngine = ENGINE_MMI_2026,
): MmiProjection {
  const {
    taxResidency,
    holdingYears,
    monthlyCondo,
    monthlyPropertyTax,
    monthlyCfe,
    monthlyPno,
    monthlyAccounting,
    monthlyEnergy,
    monthlyInternet,
    ...propertyRest
  } = input;

  const assumptions: MmiAssumptions = createMmiAssumptions({
    taxResidency,
    holdingYears,
    overrides: {
      acquisitionYear: globalAssumptions.acquisitionYear,
      inflation: globalAssumptions.inflation,
      housingPriceGrowth: globalAssumptions.housingPriceGrowth,
      personalTaxRate: globalAssumptions.personalTaxRate,
      buildingDepreciationYears: globalAssumptions.buildingDepreciationYears,
      furnitureDepreciationYears: globalAssumptions.furnitureDepreciationYears,
      landShare: globalAssumptions.landShare,
    },
  });

  return projectMmi(
    assumptions,
    {
      ...propertyRest,
      holdingYears,
      monthlyCharges: {
        condo: monthlyCondo,
        propertyTax: monthlyPropertyTax,
        cfe: monthlyCfe,
        pno: monthlyPno,
        accounting: monthlyAccounting,
        energy: monthlyEnergy,
        internet: monthlyInternet,
      },
    },
    { engine },
  );
}
